import React, { useState } from 'react';
import { Calendar, Users, Shuffle, PlayCircle, Trophy } from 'lucide-react';
import { Card, Button, Input } from '../ui';

interface Matchup {
  home: string;
  away: string;
}

interface WeekSchedule {
  week: number;
  matchups: Matchup[];
}

export function RoundRobinScheduleGenerator() {
  const [inputText, setInputText] = useState('');
  const [schedule, setSchedule] = useState<WeekSchedule[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const generateSchedule = () => {
    let teams = inputText.split('\n').map(t => t.trim()).filter(t => t.length > 0);
    
    if (teams.length < 2) {
      alert("Please enter at least 2 teams.");
      return;
    }

    // Add a BYE if odd number of teams
    if (teams.length % 2 !== 0) {
      teams.push("BYE (Rest Week)");
    }

    // Optional: shuffle teams first to randomize the schedule order
    teams = teams.sort(() => Math.random() - 0.5);

    const numTeams = teams.length;
    const numRounds = numTeams - 1;
    const halfSize = numTeams / 2;

    const generatedSchedule: WeekSchedule[] = [];

    // Array of indices to rotate
    let indices = Array.from({length: numTeams}, (_, i) => i);

    for (let round = 0; round < numRounds; round++) {
      const matchups: Matchup[] = [];
      
      for (let i = 0; i < halfSize; i++) {
        // Alternate home/away for the fixed position (index 0)
        let homeIdx = indices[i];
        let awayIdx = indices[numTeams - 1 - i];

        // Fixed team at index 0 alternates home/away to be fair
        if (i === 0 && round % 2 === 1) {
          const temp = homeIdx;
          homeIdx = awayIdx;
          awayIdx = temp;
        }

        matchups.push({
          home: teams[homeIdx],
          away: teams[awayIdx]
        });
      }

      generatedSchedule.push({
        week: round + 1,
        matchups
      });

      // Rotate indices, keeping 0 fixed
      const first = indices[0];
      const last = indices[numTeams - 1];
      const rest = indices.slice(1, numTeams - 1);
      indices = [first, last, ...rest];
    }

    setSchedule(generatedSchedule);
    setHasGenerated(true);
  };

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-8">
      {/* Header */}
      <div className="bg-emerald-900 rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-center gap-8">
        <div className="absolute inset-0 bg-emerald-800 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
        <div className="flex-1 z-10">
          <div className="flex items-center gap-2 text-emerald-300 font-bold tracking-widest uppercase mb-4 text-sm">
            <Trophy className="w-4 h-4" /> Youth Sports / Intramurals
          </div>
          <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">
            Round-Robin Schedule Maker
          </h2>
          <p className="text-emerald-100 max-w-xl text-lg">
            Stop drawing lines on a whiteboard. Paste your team names and instantly generate a mathematically perfect round-robin schedule where everyone plays exactly once.
          </p>
        </div>
        <div className="z-10 bg-emerald-800/50 p-6 rounded-2xl border border-emerald-700/50 backdrop-blur-sm">
          <Calendar className="w-24 h-24 text-emerald-400" />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        
        {/* Input Column */}
        <div className="md:col-span-1 space-y-4">
          <Card className="p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-emerald-600" /> Enter Teams
            </h3>
            <p className="text-sm text-slate-500 mb-4">Paste one team name per line. If you have an odd number of teams, the system will automatically insert a BYE week.</p>
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Cardinals&#10;Blue Jays&#10;Orioles&#10;Robins&#10;Falcons"
              className="w-full h-64 p-4 rounded-xl border border-slate-300 bg-slate-50 focus:ring-2 focus:ring-emerald-500 outline-none resize-none font-medium"
            />
            <Button 
              onClick={generateSchedule}
              disabled={inputText.trim().length === 0}
              className="w-full mt-4 h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
            >
              <Shuffle className="w-5 h-5 mr-2" /> Generate Schedule
            </Button>
          </Card>
        </div>

        {/* Results Column */}
        <div className="md:col-span-2 space-y-6">
          {!hasGenerated ? (
            <Card className="h-full min-h-[400px] flex flex-col items-center justify-center text-center p-8 bg-slate-50 border-dashed border-2">
              <PlayCircle className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-xl font-bold text-slate-400">Waiting for teams...</h3>
              <p className="text-slate-500 mt-2">Your perfectly balanced weekly matchups will appear here.</p>
            </Card>
          ) : (
            <div className="space-y-6">
              <div className="flex justify-between items-end">
                <h3 className="text-2xl font-black text-slate-800 dark:text-white">Official Schedule</h3>
                <Button variant="secondary" onClick={() => window.print()} className="hidden md:flex">
                  Print Schedule
                </Button>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {schedule.map((weekData) => (
                  <Card key={weekData.week} className="p-0 overflow-hidden border-t-4 border-t-emerald-500 shadow-md">
                    <div className="bg-slate-50 dark:bg-slate-900 px-4 py-3 border-b dark:border-slate-800 font-bold flex justify-between items-center">
                      <span>Week {weekData.week}</span>
                    </div>
                    <div className="divide-y dark:divide-slate-800">
                      {weekData.matchups.map((match, idx) => (
                        <div key={idx} className="p-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors">
                          <span className={`font-semibold w-[40%] text-right truncate ${match.home.includes('BYE') ? 'text-slate-400 font-normal italic' : ''}`}>
                            {match.home}
                          </span>
                          <span className="text-xs font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded mx-2">VS</span>
                          <span className={`font-semibold w-[40%] text-left truncate ${match.away.includes('BYE') ? 'text-slate-400 font-normal italic' : ''}`}>
                            {match.away}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
